import type { ExtractionFields, InvoiceData } from '../components/DrawerFacetTypes';
import { formatHumanDrawerDate } from './deriveNextStep';

export interface DocumentFact { key: string; label: string; value: string; evidence?: string | null }

/** Extraction used the historical amount_due key for totals too. Name what the evidence says. */
export function documentAmountLabel(evidence = '', title = ''): string {
    if (/\b(?:amount|balance|payment)\s+(?:now\s+)?due\b/i.test(evidence)) return 'Amount due';
    if (/\bcontract\s+(?:price|total|value)\b|\btotal\s+contract\b/i.test(evidence)) return 'Contract total';
    if (/\bestimat(?:e|ed)\b/i.test(evidence)) return 'Estimated amount';
    if (/\b(?:contract|agreement)\b/i.test(title)) return 'Contract amount';
    if (/\bestimate\b/i.test(title)) return 'Estimated amount';
    return 'Amount mentioned';
}

/** Preserve a stated currency; a bare number must not silently become USD. */
export function formatDocumentAmount(raw: unknown, evidence = ''): string | null {
    if (raw === null || raw === undefined || raw === '') return null;
    const text = String(raw).trim();
    if (!text || /^(?:n\/a|none|unknown|-)$/i.test(text)) return null;
    const match = text.match(/^([A-Z]{3}\s*|[£€$¥]\s*)?(-?\d[\d,]*(?:\.\d+)?)(\s*[A-Z]{3})?$/);
    if (!match) return text;
    const amount = Number(match[2].replace(/,/g, ''));
    if (!Number.isFinite(amount)) return null;
    // Only borrow a symbol when the evidence pairs it with this same amount.
    const evidenced = Array.from(evidence.matchAll(/([£€$¥])\s*(-?\d[\d,]*(?:\.\d+)?)/g))
        .find((m) => Number(m[2].replace(/,/g, '')) === amount)?.[1];
    const prefix = match[1]?.trim() || evidenced || '';
    const suffix = match[3]?.trim() || '';
    const formatted = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${prefix}${prefix.length === 3 ? ' ' : ''}${formatted}${suffix ? ` ${suffix}` : ''}`;
}

export function buildDocumentFacts(input: {
    title?: string | null;
    invoiceData?: InvoiceData | null;
    extractionFields?: ExtractionFields | null;
}): DocumentFact[] {
    const { invoiceData: invoice, extractionFields: fields, title } = input;
    const facts: DocumentFact[] = [];
    const add = (key: string, label: string, raw: unknown, evidence?: string | null) => {
        if (raw === null || raw === undefined) return;
        const value = String(raw).trim();
        if (value && !/^(?:n\/a|none|unknown|-)$/i.test(value)) facts.push({ key, label, value, evidence });
    };
    add('vendor', 'From', invoice?.vendor);
    const amountEvidence = fields?.amount_due?.evidence || '';
    add('amount', documentAmountLabel(amountEvidence, title || ''),
        formatDocumentAmount(fields?.amount_due?.value ?? invoice?.total_amount ?? invoice?.amount, amountEvidence), amountEvidence);
    const due = fields?.due_date;
    add('due', 'Due date', formatHumanDrawerDate(due?.value || invoice?.due_date), due?.evidence);
    add('reference', 'Reference', fields?.reference_id?.value || invoice?.invoice_number, fields?.reference_id?.evidence);
    add('document-date', 'Document date', formatHumanDrawerDate(fields?.document_date?.value), fields?.document_date?.evidence);
    return facts;
}
